import { useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router";
import { Cta } from "../components/Cta";
import { Section } from "../components/Section";
import { useAuth } from "../features/auth/context";
import { recordConsents } from "../services/api/consents";
import { AuthError } from "../services/auth/port";
import { usePageMeta } from "../lib/usePageMeta";
import styles from "./Login.module.css";

/**
 * /auth/callback — the return leg of the PKCE flow.
 *
 * Mounted OUTSIDE RequireAuth, deliberately: this is the page that makes you
 * authenticated, so guarding it would be a redirect loop with itself.
 *
 * It renders almost nothing on the happy path. Everything visible here is a
 * failure state, because a working sign-in should feel like it didn't happen.
 */
type Ui =
  | { kind: "working" }
  | { kind: "done"; next: string }
  | { kind: "unverified" }
  | { kind: "resent" }
  | { kind: "failed"; message: string };

export function AuthCallback() {
  usePageMeta("authCallback");
  const { completeSignIn, resendVerification } = useAuth();
  const [ui, setUi] = useState<Ui>({ kind: "working" });

  /*
   * A ref guard here, unlike in AuthProvider where one would deadlock.
   *
   * The difference is that this effect is not idempotent: it exchanges a
   * single-use authorization code. StrictMode's double-invoke would run the
   * exchange twice, and the second attempt fails — Cognito has already redeemed
   * the code — so a successful sign-in would render as "that link is not valid".
   * There is no cleanup/re-run cycle to deadlock against because nothing here
   * waits on a second run.
   */
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    completeSignIn()
      .then(async ({ next, consents }) => {
        /* Consent was collected on our /signup page before the redirect, and is
           written now with a SERVER timestamp. Best-effort: a consent-write
           failure must not strand someone on a blank page immediately after
           they created an account — the record is reconcilable, a lost signup
           is not. */
        if (consents) {
          try {
            await recordConsents(consents);
          } catch (e) {
            console.error("consent record failed", e);
          }
        }
        setUi({ kind: "done", next });
      })
      .catch((err) => {
        if (err instanceof AuthError && err.code === "email-unverified") {
          setUi({ kind: "unverified" });
          return;
        }
        setUi({
          kind: "failed",
          message:
            err instanceof AuthError && err.code === "network"
              ? "We couldn't reach the sign-in service. Try again in a moment."
              : "That sign-in link didn't work. Start again from the sign-in page.",
        });
      });
  }, [completeSignIn]);

  if (ui.kind === "done") return <Navigate to={ui.next} replace />;

  return (
    <Section
      eyebrow="Account"
      heading={ui.kind === "working" ? "Signing you in" : "Sign in"}
      tone="ink"
      level={1}
    >
      <div className={styles.wrap}>
        {ui.kind === "working" && <p>One moment…</p>}

        {ui.kind === "unverified" && (
          <div className={styles.unverified}>
            <p>
              <strong>Verify your email first.</strong> We sent you a link when you signed up.
              Your account works as soon as you&rsquo;ve used it.
            </p>
            <div>
              <Cta
                onClick={async () => {
                  await resendVerification("");
                  setUi({ kind: "resent" });
                }}
              >
                Resend the email
              </Cta>
            </div>
          </div>
        )}

        {ui.kind === "resent" && (
          <div className={styles.unverified}>
            <p>Sent. Check your inbox, then sign in again.</p>
          </div>
        )}

        {ui.kind === "failed" && (
          <>
            <p className={styles.error} role="alert">
              {ui.message}
            </p>
            <p className={styles.switchMode}>
              <Link to="/login">Back to sign in</Link>
            </p>
          </>
        )}
      </div>
    </Section>
  );
}
